// Single source of truth for which starters exist + their source.
//
// The starter source is injected into the page by the Rails controller as a
// JSON <script> block (#aicrowd-starters-data). We read it synchronously here.
//
// Why not bundle the starters through Webpack? Starters use the bare specifier
// `import ... from 'aicrowd-ui'`, which only resolves inside the production
// renderer's SES customRequire — Webpack cannot resolve it at build time, and
// feeding the raw .tsx through require.context hit Webpack-4 loader issues. The
// Explorer renders each starter by passing its raw source through
// DynamicComponentRenderer (the same path the admin form uses), so raw source
// read from the server is all we need.

export type Slot = 'Overview' | 'Banner' | 'Leaderboard' | 'SubmissionForm' | 'SubmissionDetails' | 'Rules' | 'Insights' | 'Winners' | 'Resources' | 'Submissions' | 'Discussion' | 'Registration';

export type StarterEntry = {
  slot: Slot;
  pattern: string;
  source: string;
};

const slotOrder: Slot[] = ['Overview', 'Banner', 'Leaderboard', 'SubmissionForm', 'SubmissionDetails', 'Rules', 'Insights', 'Winners', 'Resources', 'Submissions', 'Discussion', 'Registration'];

let _cache: StarterEntry[] | null = null;

// Read lazily at call time (not module-load time): with turbolinks the bundle
// evaluates once, possibly before the Explorer page's DOM (with the JSON block)
// exists. Memoize after the first successful, non-empty read.
export function getStarters(): StarterEntry[] {
  if (_cache && _cache.length) return _cache;
  const el = typeof document !== 'undefined' && document.getElementById('aicrowd-starters-data');
  if (!el || !el.textContent) return _cache || [];
  try {
    const parsed = JSON.parse(el.textContent) as StarterEntry[];
    _cache = parsed.slice().sort((a, b) => {
      const slotDiff = slotOrder.indexOf(a.slot) - slotOrder.indexOf(b.slot);
      return slotDiff !== 0 ? slotDiff : a.pattern.localeCompare(b.pattern);
    });
    return _cache;
  } catch (e) {
    console.error('[ComponentsExplorer] failed to parse starter data:', e);
    return [];
  }
}

export function findStarter(slot: Slot, pattern: string): StarterEntry | undefined {
  return getStarters().find((s) => s.slot === slot && s.pattern === pattern);
}

export function groupedBySlot(): Record<Slot, StarterEntry[]> {
  const out: any = { Overview: [], Banner: [], Leaderboard: [], SubmissionForm: [], SubmissionDetails: [], Rules: [], Insights: [], Winners: [], Resources: [], Submissions: [], Discussion: [], Registration: [] };
  getStarters().forEach((s) => out[s.slot].push(s));
  return out;
}

// Keep in sync with ChallengeReactComponent::ALLOWED_MODULES (Ruby).
export const BASE_ALLOWED_MODULES: string[] = [
  'react', 'react-dom', 'chart.js', 'framer-motion', 'aicrowd-ui',
  'marked', 'd3', 'topojson-client', 'katex', 'roughjs',
];

export const SLOT_ALLOWED_MODULES: Record<Slot, string[]> = {
  Overview:          BASE_ALLOWED_MODULES,
  Banner:            BASE_ALLOWED_MODULES,
  Leaderboard:       BASE_ALLOWED_MODULES,
  SubmissionForm:    BASE_ALLOWED_MODULES,
  SubmissionDetails: BASE_ALLOWED_MODULES,
  Rules:             BASE_ALLOWED_MODULES,
  Insights:          BASE_ALLOWED_MODULES,
  Winners:           BASE_ALLOWED_MODULES,
  Resources:         BASE_ALLOWED_MODULES,
  Submissions:       BASE_ALLOWED_MODULES,
  Discussion:        BASE_ALLOWED_MODULES,
  Registration:      BASE_ALLOWED_MODULES,
};
